import { useCallback, useState } from "react";
import { useMutation, useQuery } from "urql";
import {
  CreateScheduleConfigMutation,
  ScheduleConfigQuery,
  TermScheduleConfigQuery,
  UpdateScheduleConfigMutation,
} from "../graphql/schedule.graphql";
import { toUserFacingMessage } from "../lib/graphqlErrorMessage";

export interface ScheduleConfig {
  scheduleConfigId: string;
  termId: string;
  durationMinutes: number;
  workingHoursStart: string;
  workingHoursEnd: string;
  scheduleTitle: string;
  scheduleDescription: string | null;
  deadlineDate: string | null;
  reminderEnabled: boolean;
  reminderOneWeekBefore: boolean;
  reminderOneDayBefore: boolean;
  zoomHostEmail: string | null;
  availableFromDays: number;
  availableUntilDays: number;
  slotDisplayStartDate: string | null;
  slotDisplayEndDate: string | null;
  replyToEmail: string | null;
  useCase: string;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface UpdateScheduleConfigInput {
  scheduleConfigId: string;
  durationMinutes?: number | null;
  workingHoursStart?: string | null;
  workingHoursEnd?: string | null;
  scheduleTitle?: string | null;
  scheduleDescription?: string | null;
  deadlineDate?: string | null;
  reminderEnabled?: boolean | null;
  reminderOneWeekBefore?: boolean | null;
  reminderOneDayBefore?: boolean | null;
  zoomHostEmail?: string | null;
  availableFromDays?: number | null;
  availableUntilDays?: number | null;
  slotDisplayStartDate?: string | null;
  slotDisplayEndDate?: string | null;
  replyToEmail?: string | null;
  useCase?: string | null;
}

export function useScheduleConfig(scheduleConfigId: string | null) {
  const [result, reexecute] = useQuery({
    query: ScheduleConfigQuery,
    variables: { scheduleConfigId: scheduleConfigId ?? "" },
    pause: !scheduleConfigId,
  });

  return {
    scheduleConfig:
      (result.data?.scheduleConfig as ScheduleConfig | undefined) ?? null,
    loading: result.fetching,
    error: result.error
      ? toUserFacingMessage(result.error, "日程調整設定の取得に失敗しました")
      : null,
    refetch: () => reexecute({ requestPolicy: "network-only" }),
  };
}

export function useTermScheduleConfig(termId: string | null) {
  const [result, reexecute] = useQuery({
    query: TermScheduleConfigQuery,
    variables: { termId: termId ?? "" },
    pause: !termId,
  });

  return {
    scheduleConfig:
      (result.data?.termScheduleConfig as ScheduleConfig | undefined) ?? null,
    loading: result.fetching,
    error: result.error
      ? toUserFacingMessage(result.error, "日程調整設定の取得に失敗しました")
      : null,
    refetch: () => reexecute({ requestPolicy: "network-only" }),
  };
}

export interface CreateScheduleConfigInput {
  termId: string;
  durationMinutes?: number;
  workingHoursStart?: string | null;
  workingHoursEnd?: string | null;
  scheduleTitle?: string;
  scheduleDescription?: string | null;
  deadlineDate?: string | null;
  reminderEnabled?: boolean;
  reminderOneWeekBefore?: boolean;
  reminderOneDayBefore?: boolean;
  zoomHostEmail?: string | null;
  availableFromDays?: number;
  availableUntilDays?: number;
  slotDisplayStartDate?: string | null;
  slotDisplayEndDate?: string | null;
  replyToEmail?: string | null;
  useCase?: string | null;
}

export function useScheduleMutations() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [, executeCreate] = useMutation(CreateScheduleConfigMutation);
  const [, executeUpdate] = useMutation(UpdateScheduleConfigMutation);

  // 失敗時はサーバのエラーメッセージ（保存時バリデーション等）を呼び出し側で表示できるよう
  // error state に保持しつつ再 throw する
  const updateScheduleConfig = useCallback(
    async (data: UpdateScheduleConfigInput): Promise<ScheduleConfig | null> => {
      setLoading(true);
      setError(null);
      try {
        const result = await executeUpdate({ input: data });
        if (result.error) {
          throw new Error(
            toUserFacingMessage(
              result.error,
              "日程調整設定の更新に失敗しました",
            ),
          );
        }
        return (
          (result.data?.updateScheduleConfig as ScheduleConfig | undefined) ??
          null
        );
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : "スケジュール設定の更新に失敗しました";
        setError(message);
        throw new Error(message);
      } finally {
        setLoading(false);
      }
    },
    [executeUpdate],
  );

  const createScheduleConfig = useCallback(
    async (data: CreateScheduleConfigInput): Promise<ScheduleConfig | null> => {
      setLoading(true);
      setError(null);
      try {
        const result = await executeCreate({ input: data });
        if (result.error) {
          throw new Error(
            toUserFacingMessage(
              result.error,
              "日程調整設定の作成に失敗しました",
            ),
          );
        }
        return (
          (result.data?.createScheduleConfig as ScheduleConfig | undefined) ??
          null
        );
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : "スケジュール設定の作成に失敗しました";
        setError(message);
        throw new Error(message);
      } finally {
        setLoading(false);
      }
    },
    [executeCreate],
  );

  return {
    createScheduleConfig,
    updateScheduleConfig,
    loading,
    error,
  };
}
